import type {ReactElement} from 'react';
import React from 'react';
import {PosterEditorItemFilters, PosterEditorItemFiltersDataArray} from '@Panels/poster-editor-item-filters-panel/poster-editor-item-filters-panel.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import styles from './base-filter-thumbnails.module.scss';

interface BaseFilterThumbnailsProps {
  blackAndWhite: boolean;
  sepia: boolean;
  invert: boolean;
  onToggleFilter(filter: PosterEditorItemFilters): void;
  isSepiaHidden?: boolean;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/filters/';

export function BaseFilterThumbnails({isSepiaHidden = false, ...props}: BaseFilterThumbnailsProps): ReactElement | null {
  const getSelectedState = (filter: PosterEditorItemFilters): boolean => {
    switch (filter) {
      case PosterEditorItemFilters.BLACK_AND_WHITE:
        return props.blackAndWhite;
      case PosterEditorItemFilters.SEPIA:
        return props.sepia;
      case PosterEditorItemFilters.INVERT:
        return props.invert;
      default:
        throw new Error('unhandled filter type');
    }
  };

  const getTextFromFilter = (filter: PosterEditorItemFilters): string => {
    switch (filter) {
      case PosterEditorItemFilters.BLACK_AND_WHITE:
        return window.i18next.t('pmwjs_black_and_white_short');
      case PosterEditorItemFilters.SEPIA:
        return window.i18next.t('pmwjs_sepia');
      case PosterEditorItemFilters.INVERT:
        return window.i18next.t('pmwjs_invert');
      default:
        throw new Error('unhandled filter type');
    }
  };

  const getContent = (): (ReactElement | null)[] => {
    return PosterEditorItemFiltersDataArray.map((filterData) => {
      if (filterData.filter === PosterEditorItemFilters.SEPIA && isSepiaHidden) {
        return null;
      }
      return (
        <ContextualMenuThumbnailItem
          key={filterData.filter}
          thumbnail={
            <Thumbnail
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${filterData.thumbnail}.${getCompatibleImageFileExtension('webp')}`),
              }}
              className="radius-8"
            />
          }
          hasBorder={false}
          isSelected={getSelectedState(filterData.filter)}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={getTextFromFilter(filterData.filter)}
          onClick={(): void => {
            props.onToggleFilter(filterData.filter);
          }}
        />
      );
    });
  };

  return <ul className={`spacing-p-l-4 spacing-p-r-4 spacing-p-b-4 spacing-p-t-1 flex-h-row ${styles.list}`}>{getContent()}</ul>;
}
